import { createAsyncThunk } from '@reduxjs/toolkit';
import { normalize } from 'normalizr';
import { httpRequest } from '~/adapters/httpRequest';
import { host } from '~/config/settings/worktango.settings';
import { urls } from '~/tools/urls';
import * as transformers from '../transformers';
import * as schemas from '../schemas';
import { Condition, QuestionLogicDict } from '~/interfaces/survey.interface';

const getQuestionLogics = createAsyncThunk<QuestionLogicDict | unknown, { surveyIds: number[] }>(
  'question-logic/getQuestionLogics',
  async ({ surveyIds }, { rejectWithValue }) => {
    const queryParams = urls.queryStringify({ surveyIds }, { arrayFormat: 'indices' });
    const url = `${host}/question-logics?${queryParams}`;
    const res = await httpRequest<any>(url);

    if (res.error) return rejectWithValue(res.error);

    const logics = transformers.questionLogicsFrom(res?.payload?.questionLogics);

    const normalizedData = normalize(logics, [schemas.questionLogic]);

    return normalizedData;
  },
);

const createQuestionLogic = createAsyncThunk<
  QuestionLogicDict | unknown,
  { surveyId: number; mainSurveyElementId: number; conditions: Condition[] }
>('question-logic/createQuestionLogic', async ({ surveyId, mainSurveyElementId, conditions }, { rejectWithValue }) => {
  const url = `${host}/question-logics/${surveyId}/${mainSurveyElementId}`;
  const res = await httpRequest<any>(url, {
    method: 'POST',
    body: {
      conditions,
    },
  });

  if (res.error) return rejectWithValue(res.error);

  const logics = transformers.questionLogicsFrom(res?.payload?.questionLogics);

  const normalizedData = normalize(logics, [schemas.questionLogic]);

  return normalizedData;
});

const updateQuestionLogic = createAsyncThunk<
  QuestionLogicDict,
  { surveyId: number; mainSurveyElementId: number; conditions: Condition[] }
>('question-logic/updateQuestionLogic', async ({ surveyId, mainSurveyElementId, conditions }, { rejectWithValue }) => {
  const url = `${host}/question-logics/${surveyId}/${mainSurveyElementId}`;
  const res = await httpRequest<any>(url, {
    method: 'PATCH',
    body: {
      conditions,
    },
  });

  if (res.error) return rejectWithValue(res.error);

  const logics = transformers.questionLogicsFrom(res?.payload?.questionLogics);

  const normalizedData = normalize(logics, [schemas.questionLogic]);

  return {
    surveyId,
    mainSurveyElementId,
    ...normalizedData,
  };
});

const deleteQuestionLogic = createAsyncThunk<
  { surveyId: number; mainSurveyElementId: number },
  { surveyId: number; mainSurveyElementId: number }
>('question-logic/deleteQuestionLogic', async ({ surveyId, mainSurveyElementId }, { rejectWithValue }) => {
  const url = `${host}/question-logics/${surveyId}/${mainSurveyElementId}`;
  const res = await httpRequest<any>(url, {
    method: 'DELETE',
  });

  if (res.error) return rejectWithValue(res.error);

  return {
    surveyId,
    mainSurveyElementId,
  };
});

export const questionLogicActions = {
  getQuestionLogics,
  createQuestionLogic,
  updateQuestionLogic,
  deleteQuestionLogic,
};
